import { Crosshair, ImagePlus, X } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { Sheet } from "@/components/ui/dialog";
import { Field, Textarea } from "@/components/ui/field";
import { mensajeDe } from "@/lib/errors";

import { useCreateUserReport, type UserReport } from "./api";
import { capturarPantalla, describir, leerImagen, senalar } from "./captura";

type Tipo = "screenshot" | "correction";
type Estado = {
  tipo: Tipo;
  abierto: boolean;
  ruta: string;
  imagen: string | null;
  elemento: string | null;
  aviso: string | null;
  /** Lo escrito: vive aquí y no en la ficha, que se desmonta mientras se señala. */
  texto: string;
};

/** Dos cuadros pintados: el menú y lo que se cerró ya no están en la pantalla que se captura. */
const pintado = () => new Promise<void>((listo) => requestAnimationFrame(() => requestAnimationFrame(() => listo())));

/**
 * **Captura de pantalla y corrección puntual** de «Reportar mejora» (0182). La ruta la pone el sistema;
 * quién y cuándo, la sesión. La captura se toma **antes** de abrir el panel (si no, saldría el panel);
 * si no se puede, se elige una imagen. En la corrección, «Señalar en la pantalla» esconde el panel, deja
 * tocar el elemento y vuelve con su descripción y la captura con el elemento marcado.
 *
 * Devuelve las dos acciones del menú y el panel, que el botón de la barra dibuja.
 */
export function useReporteRapido(onGuardado: (reporte: UserReport) => void) {
  const location = useLocation();
  const [estado, setEstado] = useState<Estado | null>(null);

  async function capturar(): Promise<{ imagen: string | null; aviso: string | null }> {
    try {
      return { imagen: await capturarPantalla(), aviso: null };
    } catch (error) {
      return { imagen: null, aviso: `No se pudo tomar la captura (${mensajeDe(error)}). Puedes elegir una imagen.` };
    }
  }

  async function iniciarCaptura() {
    const ruta = location.pathname;
    await pintado();
    const { imagen, aviso } = await capturar();
    setEstado({ tipo: "screenshot", abierto: true, ruta, imagen, elemento: null, aviso, texto: "" });
  }

  function iniciarCorreccion() {
    setEstado({ tipo: "correction", abierto: true, ruta: location.pathname, imagen: null, elemento: null, aviso: null, texto: "" });
  }

  async function senalarElemento() {
    if (!estado) return;
    setEstado({ ...estado, abierto: false });
    await pintado();
    const elegido = await senalar();
    if (!elegido) {
      setEstado({ ...estado, abierto: true });
      return;
    }
    const { imagen, aviso } = await capturar();
    elegido.soltar();
    setEstado({ ...estado, abierto: true, elemento: describir(elegido.elemento), imagen: imagen ?? estado.imagen, aviso });
  }

  const hoja = estado ? (
    <Sheet
      open={estado.abierto}
      onOpenChange={(abierto) => setEstado(abierto ? { ...estado, abierto } : null)}
      title={estado.tipo === "screenshot" ? "Captura de pantalla" : "Corrección puntual"}
      description={estado.tipo === "screenshot" ? "Así se ve la pantalla. Cuéntale al owner qué mirar." : "Escribe qué está mal y, si quieres, señálalo en la pantalla."}
    >
      <FichaReporte
        estado={estado}
        onCambio={(cambio) => setEstado({ ...estado, ...cambio })}
        onSenalar={() => void senalarElemento()}
        onGuardado={(reporte) => { setEstado(null); onGuardado(reporte); }}
        onCancelar={() => setEstado(null)}
      />
    </Sheet>
  ) : null;

  return { iniciarCaptura: () => void iniciarCaptura(), iniciarCorreccion, hoja };
}

function FichaReporte({ estado, onCambio, onSenalar, onGuardado, onCancelar }: {
  estado: Estado;
  onCambio: (cambio: Partial<Estado>) => void;
  onSenalar: () => void;
  onGuardado: (reporte: UserReport) => void;
  onCancelar: () => void;
}) {
  const id = useId();
  const texto = estado.texto;
  const guardar = useCreateUserReport();
  const correccion = estado.tipo === "correction";
  const lista = (correccion ? texto.trim().length >= 3 : estado.imagen !== null) && !guardar.isPending;

  async function enviar() {
    if (!lista) return;
    try {
      onGuardado(await guardar.mutateAsync({
        kind: estado.tipo,
        screen_path: estado.ruta,
        text: texto.trim() || null,
        element: estado.elemento,
        screenshot: estado.imagen,
      }));
    } catch {
      // El motivo del servidor queda escrito abajo; lo escrito sigue aquí para reintentar.
    }
  }

  async function elegirImagen(archivo: File | undefined) {
    if (!archivo) return;
    try {
      onCambio({ imagen: await leerImagen(archivo), aviso: null });
    } catch (error) {
      onCambio({ aviso: mensajeDe(error) });
    }
  }

  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void enviar(); }}>
      <p className="text-sm text-ink-2">En <code>{estado.ruta}</code></p>
      <Field label={correccion ? "¿Qué está mal?" : "¿Qué hay que mirar? (opcional)"} htmlFor={`${id}-texto`}
        hint={correccion ? "Qué ves y cómo debería quedar." : undefined}>
        <Textarea id={`${id}-texto`} value={texto} maxLength={2000} onChange={(e) => onCambio({ texto: e.target.value })} />
      </Field>
      {correccion ? (
        <div className="grid gap-2">
          {estado.elemento ? (
            <p className="text-sm text-ink"><span className="font-semibold">Señalado: </span>{estado.elemento}</p>
          ) : null}
          <Button type="button" variant="secondary" className="justify-self-start" onClick={onSenalar}>
            <Crosshair aria-hidden /> {estado.elemento ? "Señalar otro" : "Señalar en la pantalla"}
          </Button>
        </div>
      ) : null}
      {estado.aviso ? <p role="status" className="text-sm text-warn">{estado.aviso}</p> : null}
      {estado.imagen ? (
        <Vista imagen={estado.imagen} onQuitar={correccion ? () => onCambio({ imagen: null }) : undefined} />
      ) : (
        <ElegirImagen onElegir={(f) => void elegirImagen(f)}>
          {correccion ? "Adjuntar una imagen (opcional)" : "Elegir una imagen"}
        </ElegirImagen>
      )}
      {guardar.error ? <p role="alert" className="text-sm text-bad">{mensajeDe(guardar.error)}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!lista}>{guardar.isPending ? "Guardando…" : "Guardar"}</Button>
        <Button type="button" variant="secondary" disabled={guardar.isPending} onClick={onCancelar}>Cancelar</Button>
      </div>
    </form>
  );
}

function Vista({ imagen, onQuitar }: { imagen: string; onQuitar?: () => void }) {
  return (
    <figure className="grid gap-2">
      <img src={imagen} alt="Captura de la pantalla" className="max-h-72 w-full rounded-md border border-line object-contain" />
      {onQuitar ? (
        <Button type="button" variant="secondary" className="min-h-9 justify-self-start" onClick={onQuitar}>
          <X aria-hidden /> Quitar la captura
        </Button>
      ) : null}
    </figure>
  );
}

function ElegirImagen({ onElegir, children }: { onElegir: (archivo: File | undefined) => void; children: ReactNode }) {
  return (
    <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 justify-self-start rounded-lg border border-line px-3 text-sm font-semibold text-ink hover:bg-surface-2">
      <ImagePlus aria-hidden className="size-4" />
      {children}
      <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => onElegir(e.target.files?.[0])} />
    </label>
  );
}
